import type { IClientRecentResources, IResourceType, ITreeFolder } from '@/components/common';
import type { ISelectedFolder } from '@/store/types';
import type {
  ILatestDatabaseItem,
  IResourceAnnouncementFolder,
  IResourceClientMember,
  IResourceDatabaseField,
  IResourceDatabaseFolder,
  IResourceDatabaseView,
  IResourceDatabaseViewItemsData,
  IResourceDocumentFolder,
  IResourceFolderType,
  IResourceFolderTypes,
  IResourceLinkFolder,
} from '@/api/types';
import type { ElementType } from '@/components/agent/my-availability/types';

import {
  findNestedObj,
  getFolderResourcesType,
  getRecentAnnouncements,
  getRecentDocuments,
  getRecentLinks,
  isResource,
} from '@/helpers';
import { ResourceTypeId, UserType } from '../enums';
import { AgentAccessLevel, DatabaseAccessType } from '@/api/enums';
import { RECENT_ITEMS_TO_SHOW_CLIENT_SECTION } from '@/constants';

export const combineItemsToShow = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): Record<string | number | symbol, any>[] | IResourceDatabaseViewItemsData => {
  if (!selectedFolder || selectedFolder.folderId === -1 || ['category', 'client'].includes(selectedFolder.folderType))
    return [];

  if (selectedFolder.folderType === 'view') {
    const databaseFolder = foldersList?.find(client => client.clientId === selectedFolder.clientId)?.database;
    const db = databaseFolder?.find(db => db.id === selectedFolder.folderId);
    const view = db?.folders?.find(view => view.id === selectedFolder.viewId);

    return view?.data ?? [];
  }

  const resourceToSearch = isResource(selectedFolder.folderType)
    ? foldersList?.find(client => client.clientId === selectedFolder.clientId)?.[selectedFolder.folderType] ?? []
    : [];
  const obj = findNestedObj(resourceToSearch, 'folderId', selectedFolder.folderId);
  if (!obj) return [];

  const resourceType = getFolderResourcesType(obj as IResourceFolderType | IResourceDatabaseView);

  if (!resourceType) return [];
  return obj[resourceType] || [];
};

export const combineFoldersToShow = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): IResourceAnnouncementFolder[] | IResourceDocumentFolder[] | IResourceLinkFolder[] | IResourceDatabaseFolder[] => {
  if (!selectedFolder) return [];

  switch (selectedFolder.folderType) {
    case 'category':
      const clientFolder = foldersList?.find(client => client.clientId === selectedFolder.clientId);

      if (!clientFolder) return [];
      const folders = clientFolder[ResourceTypeId[selectedFolder.folderId] as IResourceType];

      return folders || [];
    case 'announcement':
    case 'document':
    case 'link':
      if (!foldersList) return [];
      const selectedClientFolder = foldersList.find(clientFolder => clientFolder.clientId === selectedFolder.clientId);

      if (!selectedClientFolder) return [];

      const obj = findNestedObj(selectedClientFolder[selectedFolder.folderType], 'folderId', selectedFolder.folderId);
      if (!obj) return [];

      const resourceType = getFolderResourcesType(obj as ElementType<IResourceFolderTypes>);

      if (!resourceType) return [];
      return obj.folders || [];
    default:
      return [];
  }
};

export const combineDatabaseFieldsToShow = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): IResourceDatabaseField[] => {
  if (!selectedFolder || selectedFolder.folderId === -1 || selectedFolder.folderType !== 'database') return [];

  const resourceToSearch =
    foldersList?.find(client => client.clientId === selectedFolder.clientId)?.[selectedFolder.folderType] ?? [];
  const database = findNestedObj(resourceToSearch, 'folderId', selectedFolder.folderId) as IResourceDatabaseFolder;

  if (!database) return [];

  return database.fields || [];
};

export const combineSelectedViewAccessType = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): DatabaseAccessType => {
  if (!selectedFolder || !selectedFolder.viewId) return DatabaseAccessType.Public;

  const resourceToSearch = foldersList?.find(client => client.clientId === selectedFolder.clientId)?.database ?? [];
  const view = findNestedObj(resourceToSearch, 'id', selectedFolder.viewId) as IResourceDatabaseView;

  if (!view) return DatabaseAccessType.Public;

  return view.viewAccess ?? DatabaseAccessType.Public;
};

export const combineSelectedView = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): IResourceDatabaseView | null => {
  if (!foldersList || !selectedFolder || selectedFolder.folderType !== 'view') return null;

  const database = foldersList.find(client => client.clientId === selectedFolder.clientId)?.database;
  const view = database
    ?.find(db => db.id === selectedFolder.folderId)
    ?.folders?.find(view => view.id === selectedFolder.viewId);

  return view || null;
};

export const combineIsDatabaseEditable = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): boolean => {
  if (!selectedFolder || !foldersList) return false;

  const { clientId, folderId: dbId } = selectedFolder;
  const dbAccess = foldersList
    .find(client => client.clientId === clientId)
    ?.database.find(db => db.id === dbId)?.agent_access_level;

  if (dbAccess === undefined) return false;

  return [
    AgentAccessLevel.ViewAllAndEditAll,
    AgentAccessLevel.ViewAllAndEditOwn,
    AgentAccessLevel.ViewOwnAndEditOwn,
  ].includes(dbAccess);
};

export const combineFieldsFromSelectedView = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): IResourceDatabaseField[] => {
  if (!selectedFolder || selectedFolder.folderId === -1 || selectedFolder.folderType !== 'view') return [];

  const resourceToSearch = foldersList?.find(client => client.clientId === selectedFolder.clientId)?.database ?? [];
  const database = findNestedObj(resourceToSearch, 'folderId', selectedFolder.folderId) as IResourceDatabaseFolder;

  return database?.fields || [];
};

export const combineDatabaseAccessLevel = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): AgentAccessLevel => {
  if (!selectedFolder || !foldersList) return AgentAccessLevel.NoAccess;

  const { clientId, folderId: dbId } = selectedFolder;
  const dbAccess = foldersList
    .find(client => client.clientId === clientId)
    ?.database.find(db => db.id === dbId)?.agent_access_level;

  if (dbAccess === undefined) return AgentAccessLevel.NoAccess;

  return dbAccess;
};

export const combineCurrentClientFolder = (
  foldersList: ITreeFolder[] | null,
  currentClientId: number | undefined,
  userType: UserType,
): ITreeFolder | undefined => {
  if (userType === UserType.agent) {
    return;
  }

  return foldersList?.find(clientFolder => clientFolder.clientId === currentClientId);
};

export const combineClientMemberList = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
): IResourceClientMember[] => {
  if (!selectedFolder || !foldersList) return [];

  const selectedClient = foldersList.find(client => selectedFolder.clientId === client.clientId);

  return selectedClient?.clientMemberList ?? [];
};

export const combineLatestClientResources = (
  foldersList: ITreeFolder[] | null,
  selectedFolder: ISelectedFolder | null,
  latestDatabaseItems: ILatestDatabaseItem[],
): IClientRecentResources | undefined => {
  const clientToShow = foldersList?.find(client => client.clientId === selectedFolder?.folderId);

  if (!clientToShow) return;

  const recentAnnouncements = getRecentAnnouncements(clientToShow.announcement, RECENT_ITEMS_TO_SHOW_CLIENT_SECTION);
  const recentLinks = getRecentLinks(clientToShow.link, RECENT_ITEMS_TO_SHOW_CLIENT_SECTION);
  const recentDocuments = getRecentDocuments(clientToShow.document, RECENT_ITEMS_TO_SHOW_CLIENT_SECTION);

  return {
    clientName: clientToShow.clientName,
    clientId: clientToShow.clientId,
    announcements: recentAnnouncements,
    links: recentLinks,
    documents: recentDocuments,
    databases: latestDatabaseItems.filter(items => items.clientId === clientToShow.clientId),
  };
};
